/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { ChevronDown, ChevronUp, Target } from 'lucide-react';

interface FretRangeProps {
  fretRange: { start: number; end: number };
  totalFrets: number;
  onRangeChange: (range: { start: number; end: number }) => void;
  isMobile?: boolean;
}

const FretRangeSelector: React.FC<FretRangeProps> = ({
  fretRange,
  totalFrets,
  onRangeChange,
  isMobile = false
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Practical fret range presets with educational descriptions
  const practicalRanges = [
    { name: "Open Position", range: [0, 5], description: "Beginner chords & scales", icon: "🎯" },
    { name: "Lower Frets", range: [0, 7], description: "Most common playing area", icon: "📍" },
    { name: "First Octave", range: [0, 12], description: "Complete octave", icon: "🎵" },
    { name: "Middle", range: [5, 12], description: "Intermediate positions", icon: "⬆️" },
    { name: "Upper", range: [12, 17], description: "Higher register", icon: "🔝" },
    { name: "Full Neck", range: [0, totalFrets], description: "Complete fretboard", icon: "🎸" }
  ];

  const getCurrentRangeName = () => {
    const current = practicalRanges.find(preset => 
      preset.range[0] === fretRange.start && preset.range[1] === fretRange.end
    );
    return current ? current.name : 'Custom';
  };

  const handlePresetSelect = (preset: { range: number[] }) => {
    onRangeChange({ start: preset.range[0], end: preset.range[1] });
  };

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Fret Range
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {getCurrentRangeName()}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {fretRange.start}-{fretRange.end}
                </Badge>
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent>
            {/* Quick Presets */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-muted-foreground">Quick Presets</h4>
              
              {isMobile ? (
                /* Mobile: Compact grid layout */
                <div className="grid grid-cols-2 gap-2">
                  {practicalRanges.slice(0, 4).map((preset) => (
                    <Button
                      key={preset.name}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePresetSelect(preset)}
                      className="h-auto py-2 px-2 flex flex-col items-center text-center"
                      title={preset.description}
                    >
                      <span className="text-lg mb-1">{preset.icon}</span>
                      <span className="text-xs font-medium">{preset.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {preset.range[0]}-{preset.range[1]}
                      </span>
                    </Button>
                  ))}
                </div>
              ) : (
                /* Desktop: Full preset list */
                <div className="grid grid-cols-2 gap-2">
                  {practicalRanges.map((preset) => (
                    <Button
                      key={preset.name}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePresetSelect(preset)}
                      className="h-auto py-3 px-3 justify-start text-left"
                      title={preset.description}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="text-base">{preset.icon}</span>
                        <div className="flex-1">
                          <div className="text-xs font-medium">{preset.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Frets {preset.range[0]}-{preset.range[1]}
                          </div>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Range Selection */}
            <div className="space-y-3 mt-4">
              <h4 className="text-xs font-medium text-muted-foreground">Custom Range</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="text-xs font-medium">Start Fret</label>
                  <Select 
                    value={fretRange.start.toString()} 
                    onValueChange={(value) => 
                      onRangeChange({ ...fretRange, start: Number(value) })
                    }
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: totalFrets }, (_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {i === 0 ? 'Open' : `Fret ${i}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium">End Fret</label>
                  <Select 
                    value={fretRange.end.toString()} 
                    onValueChange={(value) => 
                      onRangeChange({ ...fretRange, end: Number(value) })
                    }
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: totalFrets }, (_, i) => i + 1).map(i => (
                        <SelectItem key={i} value={i.toString()}>
                          Fret {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Visual Range Indicator */}
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <div className="text-xs text-center">
                <span className="font-medium">Current Range: </span>
                <span>
                  {fretRange.start === 0 ? 'Open' : `Fret ${fretRange.start}`} to Fret {fretRange.end}
                </span>
                <span className="text-muted-foreground ml-2">
                  ({fretRange.end - fretRange.start} frets visible)
                </span>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default FretRangeSelector;
